import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { AuthShell } from "@/components/auth/AuthShell";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { useSession } from "@/context/SessionContext";
import { ApiRequestError } from "@/lib/api/client";
import { useT } from "@/lib/i18n";
import { describeError } from "@/lib/i18n/errors";

export function LoginPage() {
  const { login } = useSession();
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const next = params.get("next") ?? "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    setFields({});
    try {
      await login(email.trim(), password);
      void navigate(next, { replace: true });
    } catch (error) {
      if (error instanceof ApiRequestError) setFields(error.fields);
      setProblem(describeError(error, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      footer={
        <>
          <Seo
            description="Sign in to BS Meet to start, schedule and host video meetings. Guests never need an account to join."
            image="/screenshots/login-wide.png"
            imageAlt="The BS Meet sign-in page"
            path="/login"
            title="Sign in"
          />
          {t("auth.newHere")}{" "}
          <Link
            className="font-medium text-brand hover:underline"
            to={`/register?next=${encodeURIComponent(next)}`}
          >
            {t("nav.createAccount")}
          </Link>
        </>
      }
      subtitle={t("auth.signInSubtitle")}
      title={t("auth.welcomeBack")}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <TextField
          autoComplete="email"
          error={fields["email"]}
          label={t("auth.email")}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          required
          type="email"
          value={email}
        />
        <TextField
          autoComplete="current-password"
          error={fields["password"]}
          label={t("auth.password")}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          required
          type="password"
          value={password}
        />

        {problem !== null ? (
          <p className="text-sm text-danger" role="alert">
            {problem}
          </p>
        ) : null}

        <Button loading={busy} size="lg" type="submit">
          {t("nav.signIn")}
        </Button>
      </form>
    </AuthShell>
  );
}
